import type { StrictExtract, StrictOmit } from '@frontend/duck-tape';
import { match } from '@frontend/duck-tape';
import type { ColorVariant } from '@frontend/web-react/types';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, mergeAndConcatClasses, safeCss } from '@frontend/web-utils';
import type { BadgeProps as MBadgeProps } from '@mantine/core';
import { Badge as MBadge } from '@mantine/core';
import { Icon } from '../Icon/Icon';
import type { IconName } from '../Icon/helpers';

type BadgeColorVariant = StrictExtract<ColorVariant, 'danger' | 'primary' | 'secondary' | 'success'>;

export type BadgeProps = StrictOmit<MBadgeProps, 'classNames' | 'color' | 'variant'> & {
  bold?: boolean;
  color?: BadgeColorVariant;
  containerClassname?: ClassName;
  labelClassName?: ClassName;
  leftIconName?: IconName;
  rightIconName?: IconName;
  rounded?: boolean;
  uppercase?: boolean;
};

export const Badge = ({
  bold = false,
  color = 'primary',
  containerClassname = '',
  labelClassName,
  leftIconName,
  leftSection,
  rightIconName,
  rightSection,
  rounded = false,
  uppercase = false,
  ...props
}: BadgeProps) => {
  const classNames: MBadgeProps['classNames'] = {
    label: mergeAndConcatClasses(getLabelClassName({ bold, color, uppercase }), labelClassName),
    root: getRootClassName({ color, containerClassname, rounded }),
  };

  return (
    <MBadge
      classNames={classNames}
      leftSection={leftIconName ? <Icon color={color} name={leftIconName} /> : leftSection}
      rightSection={rightIconName ? <Icon color={color} name={rightIconName} size="xs" /> : rightSection}
      {...props}
    />
  );
};

const getLabelClassName = ({ bold, color, uppercase }: PickRequired<BadgeProps, 'bold' | 'color' | 'uppercase'>) => {
  const baseClassName = safeCss('text-caption');
  return concatClasses(
    baseClassName,
    safeCss(bold ? 'font-bold' : 'font-medium'),
    safeCss(uppercase ? 'uppercase' : 'normal-case'),
    match(color)
      .with('primary', () => safeCss(' text-textPrimary'))
      .with('success', () => safeCss(' text-textSuccess'))
      .with('danger', () => safeCss(' text-textError'))
      .with('secondary', () => safeCss(' text-textSecondary'))
      .exhaustive(),
  );
};

const getRootClassName = ({
  color,
  containerClassname,
  rounded,
}: PickRequired<BadgeProps, 'color' | 'containerClassname' | 'rounded'>) => {
  const baseClassName = safeCss('border-1 px-sm py-none gap-x-xs');

  return concatClasses(
    baseClassName,
    safeCss(rounded ? `rounded-xxl` : `rounded-sm`),
    match(color)
      .with('primary', () => safeCss(' border-textPrimary bg-surfaceAccent'))
      .with('success', () => safeCss(' bg-surfaceSuccess'))
      .with('danger', () => safeCss(' bg-surfaceError'))
      .with('secondary', () => safeCss(' bg-neutral-100'))
      .exhaustive(),
    containerClassname,
  );
};
